import { FocusEvent, KeyboardEvent, ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Tooltip } from '../Ant';
import { BarChart, ChevronDown } from '../icons';
import {
  ChartPanelHeader,
  CollapsibleChartPanelWrapper,
} from '../../assets/styles/components/dashboard.styled';

interface ICollapsibleChartPanelProps {
  title: string;
  subtitle?: ReactNode;
  summary?: ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  testId?: string;
  children: ReactNode;
}

const CollapsibleChartPanel = ({
  title,
  subtitle,
  summary,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onToggle,
  testId,
  children,
}: ICollapsibleChartPanelProps) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const expanded = controlledExpanded ?? uncontrolledExpanded;
  const bodyId = useId();
  const { t } = useTranslation();
  const toggleLabel = t(expanded ? 'common:chart.hideChart' : 'common:chart.showChart');

  const partTestId = (part: string) => (testId ? `${testId}-${part}` : undefined);

  const toggleExpanded = onToggle ?? (() => setUncontrolledExpanded((prev) => !prev));

  const dismissTooltipOnEscape = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') setKeyboardFocused(false);
  };

  const trackKeyboardFocus = (event: FocusEvent<HTMLButtonElement>) =>
    setKeyboardFocused(event.currentTarget.matches(':focus-visible'));

  return (
    <CollapsibleChartPanelWrapper
      className={expanded ? 'expanded' : undefined}
      data-testid={testId}
    >
      <ChartPanelHeader
        type="button"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={toggleExpanded}
        onKeyDown={dismissTooltipOnEscape}
        onFocus={trackKeyboardFocus}
        onBlur={() => setKeyboardFocused(false)}
        data-testid={partTestId('header')}
      >
        <span className="chart-panel-icon">
          <BarChart />
        </span>
        <span className="chart-panel-heading">
          <Text $fontsize="13px" $lineheight="18px" $weight={600} $color="title">
            {title}
          </Text>
          {subtitle && (
            <Text
              $fontsize="11px"
              $weight={500}
              $lineheight="17px"
              $color="description"
              className="chart-panel-subtitle"
              data-testid={partTestId('subtitle')}
            >
              {subtitle}
            </Text>
          )}
        </span>
        {summary && (
          <Text
            $fontsize="13px"
            $lineheight="18px"
            $weight={600}
            $color="primary"
            className="flex-shrink-0"
            data-testid={partTestId('summary')}
            $width="auto"
          >
            {summary}
          </Text>
        )}
        {/* open stays undefined unless keyboard-focused, so antd keeps handling hover itself */}
        <Tooltip
          title={toggleLabel}
          placement="top"
          arrow={false}
          open={keyboardFocused || undefined}
        >
          <span className="flex-shrink-0" data-testid={partTestId('toggle')}>
            <ChevronDown className="chart-panel-chevron" />
          </span>
        </Tooltip>
      </ChartPanelHeader>
      <div id={bodyId} className="chart-panel-body" data-testid={partTestId('body')}>
        <div className="chart-panel-content">
          <div className="chart-panel-chart">{children}</div>
        </div>
      </div>
    </CollapsibleChartPanelWrapper>
  );
};

export default CollapsibleChartPanel;
